import React from "react";
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Modal,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  maxHeight: '90vh',
  overflow: 'auto'
};

const CenterView = ({ open, onClose, center }) => {
  const userData = useSelector((state) => state.user.data);
  const isAdmin = userData?.role === 'admin';
  const amenitiesList = String(center?.amenities || '').split(',');

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date instanceof Date) {
      return date.toLocaleString();
    }
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate().toLocaleString();
    }
    return 'N/A';
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="center-modal-title"
      aria-describedby="center-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <Typography variant="h6" component="h2">
            Center Details
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        {center && (
          <Box sx={{ p: 3 }}>
            {/* Photos Grid */}
            {center.photos && center.photos.length > 0 && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Photos
                </Typography>
                <Grid container spacing={2}>
                  {center.photos.map((photo, index) => (
                    <Grid item xs={3} key={index} sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={photo.url}
                        alt={photo.name || `Photo ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{center.centerName || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Building Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{center.buildingType || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Building Grade</Typography>
                    <Typography variant="body1">{center.buildingGrade || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Active</Typography>
                    <Typography variant="body1">{center.active ? 'Yes' : 'No'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Location Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Location Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}>{center.address || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>City</Typography>
                    <Typography variant="body1">{center.city || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Locality</Typography>
                    <Typography variant="body1">{center.locality || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Google Maps Location</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      wordBreak: 'break-all'
                    }}>{center.googleMapsLocation || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Manager Name</Typography>
                    <Typography variant="body1">{center.centerManagerName || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Manager Phone</Typography>
                    <Typography variant="body1">{center.centerManagerPhone || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center Manager Email</Typography>
                    <Typography variant="body1">{center.centerManagerEmail || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operational Hours</Typography>
                    <Typography variant="body1">{center.operationalHours || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operational From</Typography>
                    <Typography variant="body1">{center.operationalFrom || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>List Price</Typography>
                    <Typography variant="body1">₹{center.listPrice || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Cafeteria Capacity</Typography>
                    <Typography variant="body1">{center.cafeteriaCapacity || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Car Parking Cost</Typography>
                    <Typography variant="body1">{center.carParkingCost || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Bike Parking Cost</Typography>
                    <Typography variant="body1">{center.bikeParkingCost || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '60px'
                    }}>{center.description || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Amenities</Typography>
                    <Typography variant="body1" sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}>
                      {amenitiesList.length > 0
                        ? amenitiesList
                          .map(amenity => amenity.trim())
                          .filter(Boolean)
                          .join(', ')
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Record Details - Only visible to admin */}
              {isAdmin && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                    Record Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Center ID</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{center.id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operator ID</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{center.operatorId || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created By</Typography>
                      <Typography variant="body1">{center.createdBy || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created At</Typography>
                      <Typography variant="body1">{formatDate(center.createdAt)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Updated By</Typography>
                      <Typography variant="body1">{center.lastUpdatedBy || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Updated At</Typography>
                      <Typography variant="body1">{formatDate(center.lastUpdatedAt)}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default CenterView;
